import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle, Mail, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import {
  AUTH_SECONDARY_ACTION_CLASSES,
  AuthPageHeader,
  AuthPageLayout,
} from "@/components/auth/AuthPageLayout";

interface EmailVerificationPendingProps {
  email: string;
  onResendSuccess?: () => void;
  /**
   * URL to redirect to after the user clicks the verification email link.
   * Defaults to the /auth/confirm callback which then forwards to `/`.
   * Callers (e.g. SignUp with an invite_token) should pass a more specific
   * destination via the ?next=... query param so the user lands in the right place.
   */
  redirectUrl?: string;
}

export function EmailVerificationPending({
  email,
  onResendSuccess,
  redirectUrl = `${window.location.origin}/auth/confirm?next=%2F`,
}: EmailVerificationPendingProps) {
  const { t } = useLanguage();
  const [isResending, setIsResending] = useState(false);
  const [canResend, setCanResend] = useState(true);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    let timer: number;
    if (countdown > 0) {
      timer = window.setTimeout(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else if (countdown === 0 && !canResend) {
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [countdown, canResend]);

  const handleResendVerification = async () => {
    setIsResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: email,
        options: {
          emailRedirectTo: redirectUrl,
        },
      });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success(t.auth.verificationEmailSentToast);
        setCanResend(false);
        setCountdown(60); // 60 second cooldown
        onResendSuccess?.();
      }
    } catch {
      toast.error(t.auth.failedToResendEmail);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <AuthPageLayout>
      <AuthPageHeader
        title={t.auth.checkYourEmail}
        description={t.auth.checkEmailToActivate}
        icon={<Mail className="h-6 w-6" />}
        iconClassName="bg-blue-100 text-blue-600"
      />

      <div className="space-y-5">
        <Alert className="rounded-xl">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>
            {t.auth.sentVerificationLinkTo} <strong>{email}</strong>
          </AlertDescription>
        </Alert>

        <p className="text-[14px] leading-6 text-muted-foreground">{t.auth.clickLinkToActivate}</p>

        <div className="space-y-3">
          <Button
            variant="outline"
            onClick={handleResendVerification}
            disabled={isResending || !canResend}
            className={AUTH_SECONDARY_ACTION_CLASSES}
          >
            {isResending ? (
              t.common.sending
            ) : !canResend ? (
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                {t.auth.resendIn.replace("{countdown}", String(countdown))}
              </span>
            ) : (
              t.auth.resendVerificationEmail
            )}
          </Button>

          <Button
            variant="ghost"
            onClick={() => (window.location.href = "/login")}
            className="h-11 w-full rounded-xl text-[14px] font-semibold"
          >
            {t.auth.backToLogin}
          </Button>
        </div>
      </div>
    </AuthPageLayout>
  );
}
